/**
 * Point d'entrée du moteur (PRD §6) : `simulate(baseline, params, programme, financing, choices, seed, content?)`.
 * Boucle du mandat : vote du budget (censure possible) → année simulée → événements → fin de mandat (verdict).
 * Pur et déterministe : la seule source d'aléa est `createRng(seed)` (censure, probabilités d'événements).
 */
import { applyEvents, buildScope, detectEvents, scheduledEvents } from "./events.js";
import { previewFinancing } from "./financing.js";
import { param, paramOpt } from "./params.js";
import { measurePoliticalCostAt, optionPoliticalCostAt } from "./politics.js";
import { createRng, deriveSeed } from "./rng.js";
import type { Rng } from "./rng.js";
import { cloneState, initState, outlookAt, PCT, resolveMeasures, validateFinancing, viewBaseline } from "./state.js";
import type { BaselineView, EngineState } from "./state.js";
import type {
  Baseline,
  Choices,
  Financing,
  GameEvent,
  Measure,
  ParamSet,
  Programme,
  SimContent,
  SimOptions,
  Simulate,
  Timeline,
  Verdict,
  VerdictRule,
  YearState,
} from "./types.js";
import { simulateYear } from "./year.js";
import type { YearInputs } from "./year.js";

const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));

/** Contexte immuable d'une partie (baseline résolue, catalogue, entrées du joueur). */
export interface Game {
  view: BaselineView;
  params: ParamSet;
  programme: Programme;
  financing: Financing;
  measures: Measure[];
  catalogue: Measure[];
  events: GameEvent[];
  eventsById: Record<string, GameEvent>;
  mandate_years: number;
  /** Mode réaliste (ADR 0015) : pas de censure ni de crise de dette par l'indice de confiance, pas de frein de confiance. */
  realistic: boolean;
}

export function createGame(baseline: Baseline, params: ParamSet, programme: Programme, financing: Financing, content?: SimContent, options?: SimOptions): Game {
  // Bornes des leviers et du rabot (params) : un lien de partage forgé ne doit pas atteindre le moteur (REDTEAM, ADR 0005).
  validateFinancing(params, financing);
  const resolved = resolveMeasures(programme, content);
  const events = content?.events ?? [];
  const eventsById: Record<string, GameEvent> = {};
  for (const e of events) eventsById[e.id] = e;
  const view = viewBaseline(baseline, params);
  for (const id of Object.keys(financing.mission_cuts ?? {})) {
    if (!view.missions.some((m) => m.id === id)) throw new Error(`financing : économie ciblée sur une mission inconnue « ${id} »`);
  }
  view.plf_anchor_pct = plfAnchor(baseline, params, view);
  return {
    view,
    params,
    programme,
    financing,
    measures: resolved.measures,
    catalogue: content?.measures ?? [],
    events,
    eventsById,
    mandate_years: param(params, "mandate_years"),
    realistic: options?.realistic === true,
  };
}

/**
 * Calage du tendanciel « à politique inchangée » (ADR 0009 §6, révisé ADR 0014 §3). La trajectoire officielle (outlook) INCLUT les
 * mesures nouvelles du PLF/PLFSS de la première année (`outlook_policy_measures_bn`, 43 Md€ au PLF 2027) : la cible du tendanciel
 * est donc solde officiel − mesures / PIB. Le calage est l'écart, en % du PIB, entre cette cible et le solde que le moteur simule
 * sans programme, financement ni événement ; c'est une ressource (ou charge) de l'État proportionnelle au PIB, sans effet de demande.
 * 0 sans outlook ou si `outlook_anchor_first_year` vaut 0. Sans `outlook_policy_measures_bn` : cible = solde officiel (v0.3 moteur).
 */
export function plfAnchorParts(baseline: Baseline, params: ParamSet, view: BaselineView): { target: number; simulated: number; measures_pct: number } | null {
  if (!view.outlook || !view.start.from_outlook) return null;
  const official = outlookAt(view.outlook, "apu_deficit_pct_gdp", view.base_year + 1);
  if (official === undefined || !view.outlook.years.includes(view.base_year + 1)) return null;
  const s0 = initState(baseline, params, { measure_ids: [], hyp: {} }, { measures: [], byId: {} });
  const r = simulateYear(s0, { view: { ...view, plf_anchor_pct: 0 }, params, financing: { revenue_raises: {}, across_the_board_cut: 0 }, measures: [], eventsById: {} });
  const measuresPct = ((paramOpt(params, "outlook_policy_measures_bn") ?? 0) / (r.year.kpis["gdp_bn"] ?? NaN)) * PCT;
  return { target: official - measuresPct, simulated: r.report.deficit_pct, measures_pct: measuresPct };
}

function plfAnchor(baseline: Baseline, params: ParamSet, view: BaselineView): number {
  if ((paramOpt(params, "outlook_anchor_first_year") ?? 0) === 0) return 0;
  const parts = plfAnchorParts(baseline, params, view);
  return parts ? parts.target - parts.simulated : 0;
}

function yearInputs(g: Game): YearInputs {
  return { view: g.view, params: g.params, financing: g.financing, measures: g.measures, eventsById: g.eventsById, realistic: g.realistic };
}

/** Crédits tendanciels des missions l'année `turn + 1` (base du rabot, des économies ciblées et du coût politique du financement). */
function missionsNext(g: Game, s: EngineState): { total: number; credits: Record<string, number> } {
  const f = s.missions_trend * (1 + (s.inflation + param(g.params, "mission_trend_real_growth_pct")) / PCT);
  const credits: Record<string, number> = {};
  for (const m of g.view.missions) credits[m.id] = m.credits * f;
  return { total: g.view.missions_total * f, credits };
}

/**
 * Coût politique prévu de l'année `turn + 1` (règle symétrique, ADR 0009) : financement + mesures + décisions de crise en vigueur.
 * Sert au vote du budget (censure si le capital ne le couvre pas).
 */
export function projectedPoliticalCost(g: Game, s: EngineState): number {
  const next = missionsNext(g, s);
  let cost = previewFinancing(g.params, g.financing, next.total, next.credits).political_cost;
  for (const m of g.measures) {
    if (s.retired.includes(m.id)) continue;
    cost += measurePoliticalCostAt(g.params, m, s.turn - (s.adopted[m.id] ?? 0), s.hyp[m.id]);
  }
  for (const ev of s.active_events) {
    const opt = g.eventsById[ev.event_id]?.options.find((o) => o.id === ev.option_id);
    if (opt) cost += optionPoliticalCostAt(g.params, opt, s.turn - ev.since);
  }
  return cost;
}

/**
 * Vote du budget de l'année suivante (ADR 0005 : censure liée à la majorité). Deux risques indépendants, un seul tirage :
 * capital politique insuffisant pour le coût politique prévu de l'année (financement, mesures, décisions de crise : règle
 * symétrique, ADR 0009) → `censure_probability` ; majorité sous `majority_loss_threshold` → `censure_probability_per_majority_pt`
 * par point manquant (plafonné à 1). Le tirage est consommé à chaque vote (flux d'aléa dédié, aligné d'une partie à l'autre).
 */
export function vote(g: Game, s: EngineState, rng: Rng): string | null {
  const P = (id: string): number => param(g.params, id);
  const cost = projectedPoliticalCost(g, s);
  const pCapital = s.capital - cost < 0 ? P("censure_probability") : 0;
  const gap = P("majority_loss_threshold") - s.majority;
  const pMajority = gap > 0 ? clamp(gap * P("censure_probability_per_majority_pt"), 0, 1) : 0;
  const p = 1 - (1 - pCapital) * (1 - pMajority);
  const u = rng.next();
  if (p > 0 && u < p) {
    const why =
      pCapital > 0 && pMajority > 0
        ? `capital politique épuisé (${Math.round(s.capital)} − ${Math.round(cost)} < 0) et majorité perdue (${Math.round(s.majority)} / 100)`
        : pCapital > 0
          ? `capital politique épuisé (${Math.round(s.capital)} − ${Math.round(cost)} < 0)`
          : `majorité perdue à l'Assemblée (${Math.round(s.majority)} / 100), motion de censure adoptée`;
    return `censure : budget ${s.base_year + s.turn + 1} rejeté, ${why}`;
  }
  return null;
}

const fmt1 = (x: number): string => x.toFixed(1).replace(".", ",");

/**
 * Cible institutionnelle de solde pour l'année `year` (ADR 0014 §4) : trajectoire du plan budgétaire et structurel de moyen terme
 * telle que publiée dans `baseline.outlook` (année demandée, sinon dernière année publiée avant elle, signalée) ; sans outlook, la
 * règle du traité (−`deficit_rule_pct_gdp`). Aucun seuil choisi pour le jeu.
 */
export function deficitTarget(params: ParamSet, view: BaselineView, year: number): { value: number; year: number; source: "outlook" | "treaty" } {
  const o = view.outlook;
  if (o) {
    const ser = o.series["apu_deficit_pct_gdp"];
    if (ser) {
      for (let i = o.years.length - 1; i >= 0; i--) {
        const y = o.years[i] ?? 0;
        const v = ser[i];
        if (y <= year && v !== null && v !== undefined) return { value: v, year: y, source: "outlook" };
      }
    }
  }
  return { value: -param(params, "deficit_rule_pct_gdp"), year, source: "treaty" };
}

/**
 * Règles explicites du verdict (ADR 0009 §5, révisé ADR 0014 §4), renvoyées pour affichage. Réélu ⇔ toutes les règles tenues :
 * (a) déficit : solde APU final meilleur que celui du départ (réduction stricte) OU au moins égal à la trajectoire européenne du
 * plan de moyen terme (`deficitTarget`) — ancrages institutionnels, pas de seuil de jeu ; (b) majorité finale ≥
 * `verdict_majority_min` ; (c) pouvoir d'achat final ≥ `verdict_pp_min` (indice, 100 = tendanciel) ; (d) confiance des marchés ≥
 * `confidence_low` ; (e) capital politique électoral (capital + `verdict_confidence_weight` × (confiance − `confidence_low`)) >
 * `reelection_capital_min` ; (f) cohésion > `reelection_cohesion_min`. (b)–(f) : conventions de jeu (`convention_de_jeu`).
 */
export function verdictRules(params: ParamSet, last: YearState, view: BaselineView): VerdictRule[] {
  const P = (id: string): number => param(params, id);
  const k = last.kpis;
  const deficit = k["deficit_pct_gdp"] ?? 0;
  const improvement = deficit - view.start.deficit_pct;
  const year = last.year;
  const target = deficitTarget(params, view, year);
  const rule = (r: Omit<VerdictRule, "ok">): VerdictRule => ({ ...r, ok: r.comparator === "min" ? r.value >= r.threshold : r.value > r.threshold });
  const altOk = improvement > 0;
  const targetLabel =
    target.source === "outlook"
      ? `trajectoire du plan de moyen terme (${fmt1(-target.value)} % du PIB${target.year !== year ? ` en ${target.year}, dernière année publiée` : ""})`
      : `règle du traité (${fmt1(-target.value)} % du PIB)`;
  const def = rule({
    id: "deficit",
    unit: "pct_gdp",
    kind: "economy",
    label: `Déficit public ${year} réduit par rapport à ${view.start.year} (${fmt1(-view.start.deficit_pct)} %), ou conforme à la ${targetLabel}`,
    value: deficit,
    threshold: target.value,
    comparator: "min",
    param: target.source === "outlook" ? "baseline.outlook.apu_deficit_pct_gdp" : "deficit_rule_pct_gdp",
    alt: { label: `Réduction du déficit depuis ${view.start.year} (pt de PIB)`, value: improvement, threshold: 0, param: "baseline.outlook.apu_deficit_pct_gdp", ok: altOk },
  });
  def.ok = def.ok || altOk;
  const confidence = k["confidence"] ?? 0;
  const confLow = P("confidence_low");
  const electoral = (k["political_capital"] ?? 0) + P("verdict_confidence_weight") * (confidence - confLow);
  return [
    def,
    rule({ id: "majority", unit: "index", kind: "politics", label: `Majorité au Parlement en ${year} d'au moins ${Math.round(P("verdict_majority_min"))} / 100`, value: k["parliament_majority"] ?? 0, threshold: P("verdict_majority_min"), comparator: "min", param: "verdict_majority_min" }),
    rule({ id: "purchasing_power", unit: "index", kind: "society", label: `Pouvoir d'achat en ${year} d'au moins ${fmt1(P("verdict_pp_min"))} (100 = sans changement de politique)`, value: k["purchasing_power_index"] ?? 0, threshold: P("verdict_pp_min"), comparator: "min", param: "verdict_pp_min" }),
    rule({ id: "confidence", unit: "index", kind: "economy", label: `Confiance des marchés d'au moins ${Math.round(confLow)} / 100`, value: confidence, threshold: confLow, comparator: "min", param: "confidence_low" }),
    rule({ id: "capital", unit: "index", kind: "politics", label: `Capital politique électoral supérieur à ${Math.round(P("reelection_capital_min"))}`, value: electoral, threshold: P("reelection_capital_min"), comparator: "min_strict", param: "reelection_capital_min" }),
    rule({ id: "cohesion", unit: "index", kind: "society", label: `Cohésion sociale supérieure à ${Math.round(P("reelection_cohesion_min"))} / 100`, value: k["cohesion"] ?? 0, threshold: P("reelection_cohesion_min"), comparator: "min_strict", param: "reelection_cohesion_min" }),
  ];
}

/**
 * Verdict de fin de mandat : réélu ⇔ toutes les règles de `verdictRules` tenues ; score pondéré (params `score_*`).
 * @deprecated ADR 0015 : la fin de mandat est jugée par le bilan chiffré comparé (`scorecard`), pas par « réélu / battu ». Conservé
 * pour la compatibilité (scripts de red team, tests historiques) ; l'UI ne l'affiche plus.
 */
export function verdict(params: ParamSet, last: YearState, view: BaselineView): Verdict {
  const P = (id: string): number => param(params, id);
  const k = last.kpis;
  const debt0 = (view.start.debt / view.start.gdp) * PCT;
  const part = (x: number, lo: number, hi: number): number => clamp((x - lo) / (hi - lo), 0, 1);
  const weights = ["score_w_debt", "score_w_unemployment", "score_w_poverty", "score_w_cohesion", "score_w_confidence"];
  const score =
    P("score_w_debt") * part(debt0 - (k["debt_pct_gdp"] ?? debt0), -P("score_debt_span_pt"), P("score_debt_span_pt")) +
    P("score_w_unemployment") * part(view.unemployment - (k["unemployment_pct"] ?? view.unemployment), -P("score_unemployment_span_pt"), P("score_unemployment_span_pt")) +
    P("score_w_poverty") * part(view.poverty - (k["poverty_pct"] ?? view.poverty), -P("score_poverty_span_pt"), P("score_poverty_span_pt")) +
    P("score_w_cohesion") * part(k["cohesion"] ?? 0, 0, PCT) +
    P("score_w_confidence") * part(k["confidence"] ?? 0, 0, PCT);
  const rules = verdictRules(params, last, view);
  const blocked = rules.filter((r) => !r.ok).map((r) => r.id);
  return {
    reelected: blocked.length === 0,
    score: Math.round(score),
    score_max: Math.round(weights.reduce((a, id) => a + P(id), 0)),
    blocked_by: blocked,
    rules,
  };
}

/** KPIs du point de départ (fin de l'année de base) : ce que l'UI affiche comme « départ ». */
export function startKpis(g: Game, s: EngineState): Record<string, number> {
  const st = g.view.start;
  return {
    year: st.year,
    deficit_pct_gdp: st.deficit_pct,
    debt_pct_gdp: s.debt_ratio,
    debt_bn: s.debt,
    gdp_bn: s.gdp,
    growth_pct: st.growth,
    inflation_pct: st.inflation,
    oat10_pct: st.oat10,
    apparent_rate_pct: st.apparent_rate,
    interest_bn: (st.debt_prev * st.apparent_rate) / PCT,
    unemployment_pct: s.unemployment,
    poverty_pct: s.poverty,
    purchasing_power_index: s.purchasing_power,
    parliament_majority: s.majority,
    political_capital: s.capital,
    confidence: s.confidence,
    cohesion: s.cohesion,
    from_outlook: st.from_outlook ? 1 : 0,
    plf_anchor_pct: g.view.plf_anchor_pct,
  };
}

interface Run {
  timeline: Timeline;
  state: EngineState;
  game: Game;
}

/**
 * Boucle du mandat, partagée par `simulate` et `simulateWithState`. Deux flux d'aléa indépendants dérivés de la graine : votes
 * (un tirage par budget) et événements (probabilités, risques). Avec `options.schedule`, la séquence d'événements est imposée.
 */
function runMandate(baseline: Baseline, params: ParamSet, programme: Programme, financing: Financing, choices: Choices, seed: number, content: SimContent | undefined, options: SimOptions | undefined, upTo: number): Run {
  const g = createGame(baseline, params, programme, financing, content, options);
  const voteRng = createRng(deriveSeed(seed, 1));
  const eventRng = createRng(seed);
  let s = initState(baseline, params, programme, { measures: g.measures, byId: Object.fromEntries(g.catalogue.map((m) => [m.id, m])) });
  const start = startKpis(g, s);
  const years: YearState[] = [];
  const floor = param(params, "game_over_confidence_floor");
  const n = Math.min(upTo, g.mandate_years);
  const done = (game_over: Timeline["game_over"]): Run => ({ timeline: { base_year: g.view.base_year, start, years, game_over }, state: s, game: g });
  for (let i = 0; i < n; i++) {
    // Mode réaliste (ADR 0015) : le vote du budget n'est pas simulé (capital politique, majorité et censure sont des conventions de
    // jeu) ; le tirage est tout de même consommé pour garder le flux d'aléa des votes aligné d'un mode à l'autre.
    const reason = g.realistic ? (voteRng.next(), null) : vote(g, s, voteRng);
    if (reason !== null) return done({ year: s.base_year + s.turn + 1, reason });
    const r = simulateYear(s, yearInputs(g));
    s = r.state;
    const fired = options?.schedule ? scheduledEvents(s.turn, options.schedule, g.eventsById) : detectEvents(s, buildScope(s, r.report, g.catalogue, params), g.events, choices, eventRng);
    applyEvents(s, fired, params, r.year, eventRng, g.catalogue);
    years.push(r.year);
    if (!g.realistic && s.confidence <= floor) {
      r.year.journal.push("Les marchés ferment le robinet : la France ne trouve plus preneur pour sa dette aux conditions courantes.");
      return done({ year: r.year.year, reason: `crise de dette : confiance des marchés à ${Math.round(s.confidence)}` });
    }
  }
  return done(null);
}

export const simulate: Simulate = (baseline, params, programme, financing, choices, seed, content, options) => {
  const run = runMandate(baseline, params, programme, financing, choices, seed, content, options, Number.POSITIVE_INFINITY);
  const tl = run.timeline;
  const last = tl.years[tl.years.length - 1];
  if (last && tl.game_over === null && tl.years.length === run.game.mandate_years) tl.verdict = verdict(params, last, run.game.view);
  return tl;
};

/**
 * Rejoue un mandat et renvoie aussi l'état interne final (pour `projectRemaining`). Même déterminisme que `simulate`.
 */
export function simulateWithState(
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  choices: Choices,
  seed: number,
  content: SimContent | undefined,
  upToYears: number,
  options?: SimOptions,
): { timeline: Timeline; state: EngineState; game: Game } {
  return runMandate(baseline, params, programme, financing, choices, seed, content, options, upToYears);
}

/**
 * Projection « si rien ne change » : années restantes du mandat depuis un état, sans nouvel événement ni censure.
 * Les effets des événements déjà choisis continuent de s'appliquer.
 */
export function projectRemaining(game: Game, state: EngineState): YearState[] {
  let s = cloneState(state);
  const out: YearState[] = [];
  while (s.turn < game.mandate_years) {
    const r = simulateYear(s, yearInputs(game));
    s = r.state;
    out.push(r.year);
  }
  return out;
}

/** Écarts « mesure seule − tendanciel », année par année (préfixe `d_`, vocabulaire partagé avec l'UI, ADR 0004). */
export interface TrajectoryPoint {
  year: number;
  d_balance_apu_bn: number;
  d_deficit_pct_gdp: number;
  d_debt_pct_gdp: number;
  d_growth_pt: number;
  d_unemployment_pt: number;
  d_trade_bn: number;
  d_poverty_pt: number;
  d_cohesion: number;
  d_co2_mt: number;
  /** Pouvoir d'achat (points d'indice, 100 = tendanciel) et majorité au Parlement (points) — ADR 0005. Toujours renseignés par le moteur ; optionnels pour la compatibilité des mocks UI. */
  d_purchasing_power?: number;
  d_parliament_majority?: number;
}

/**
 * Effet d'une mesure isolée sur le mandat (PRD §11.1 « Effet sur 5 ans ») : simulation de la mesure seule, financée
 * par la dette, sans événement, comparée au budget tendanciel. `hyp` : valeur du curseur (défaut : celle de la mesure).
 */
export function measureTrajectory(baseline: Baseline, params: ParamSet, catalogue: Measure[], measureId: string, hyp?: number): TrajectoryPoint[] {
  const m = catalogue.find((x) => x.id === measureId);
  if (!m) throw new Error(`measureTrajectory : mesure inconnue « ${measureId} »`);
  const noFin: Financing = { revenue_raises: {}, across_the_board_cut: 0 };
  const content: SimContent = { measures: catalogue, events: [] };
  const run = (programme: Programme): YearState[] => {
    const g = createGame(baseline, params, programme, noFin, content);
    let s = initState(baseline, params, programme, { measures: g.measures, byId: {} });
    const out: YearState[] = [];
    for (let i = 0; i < g.mandate_years; i++) {
      const r = simulateYear(s, yearInputs(g));
      s = r.state;
      out.push(r.year);
    }
    return out;
  };
  const base = run({ measure_ids: [], hyp: {} });
  const withM = run({ measure_ids: [measureId], hyp: hyp !== undefined ? { [measureId]: hyp } : {} });
  return withM.map((y, i) => {
    const b = base[i];
    if (!b) throw new Error("measureTrajectory : longueurs incohérentes");
    const d = (key: string): number => (y.kpis[key] ?? 0) - (b.kpis[key] ?? 0);
    return {
      year: y.year,
      d_balance_apu_bn: y.account.balance_apu - b.account.balance_apu,
      d_deficit_pct_gdp: d("deficit_pct_gdp"),
      d_debt_pct_gdp: d("debt_pct_gdp"),
      d_growth_pt: d("growth_pct"),
      d_unemployment_pt: d("unemployment_pct"),
      d_trade_bn: d("trade_balance_bn"),
      d_poverty_pt: d("poverty_pct"),
      d_cohesion: d("cohesion"),
      d_co2_mt: d("co2_mt"),
      d_purchasing_power: d("purchasing_power_index"),
      d_parliament_majority: d("parliament_majority"),
    };
  });
}
